import React from 'react';

// Styles & Assets
import styles from './DialogDeviceSelect.module.css';

// Services
import { isDeviceOnLoan } from '../../../services/DevicesHelpers';
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useEffect, useState } from 'react';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// Components
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import DropdownSelector from '../../UI/DropdownSelector/DropdownSelector';


// Language
import { useTranslation } from 'react-i18next';
import { getUser } from '../../../services/Auth';

// Interfaces
import { Device, DeviceLoanPatient, DeviceType } from '../../../interfaces/Device';

interface DeviceModelManufacturer {
    id: number,
    name: string
    check: boolean
    model_id?: number | null
    manufacturer_id?: number | null
    devices: Device[]
}
interface ModelManufacturerDeviceType extends DeviceType {
    modelsDevices: DeviceModelManufacturer[]
}
interface Props {
    showModal: React.Dispatch<React.SetStateAction<boolean>>
    patientID: number | undefined
    getAllLoanDevices: () => void
}
const DialogDeviceSelect: React.FC<Props> = ({ showModal, patientID, getAllLoanDevices }) => {

    const { t } = useTranslation();

    const toast = useToast();

    const [devicesOptions, setDevicesOptions] = useState<ModelManufacturerDeviceType[]>([]);

    const { sendRequest: getDeviceTypes } = useHttp<DeviceType[]>();
    const { sendRequest: getDevices } = useHttp<Device[]>();
    const { sendRequest: LinkDevice } = useHttp<DeviceLoanPatient>();

    const handleSetDevices = (devices: Device[], devicesTypes: DeviceType[]) => {
        const filteredDevices = devices.filter(device => !isDeviceOnLoan(device));
        const devicesWithTypes = devicesTypes.map(deviceType => {
            const newDeviceTypeModel: ModelManufacturerDeviceType = { ...deviceType, modelsDevices: [] };
            const devicesType = filteredDevices.filter(device => device.device_type_id === newDeviceTypeModel.id);
            const devicesModelManufacturer: DeviceModelManufacturer[] = [];
            devicesType.forEach(device => {
                const existingDeviceModel = devicesModelManufacturer.find(deviceModel => deviceModel.model_id === device.device_model_id
                    && deviceModel.manufacturer_id === device.model?.device_manufacturer_id);
                if (existingDeviceModel) existingDeviceModel.devices.push(device);
                else devicesModelManufacturer.push({
                    id: devicesModelManufacturer.length + 1,
                    name: !device.model?.manufacturer?.name || !device.model?.name ? 'Not defined' :
                        (device.model?.manufacturer?.name + ' ' + device.model?.name),
                    check: false,
                    devices: [device],
                    model_id: device.device_model_id,
                    manufacturer_id: device.model?.device_manufacturer_id
                });
            });
            devicesModelManufacturer.forEach(deviceModel => deviceModel.name = deviceModel.name += ` (${deviceModel.devices.length})`);
            newDeviceTypeModel.modelsDevices = devicesModelManufacturer;
            return newDeviceTypeModel;
        });
        setDevicesOptions(devicesWithTypes);
    };

    useEffect(() => {
        getDeviceTypes({
            url: getEnvironmentApiUrl() + 'device_type/all',
            method: 'POST',
            body: { params: {}, filter: {} }
        }, (devicesTypes: DeviceType[]) =>
            getDevices({
                url: getEnvironmentApiUrl() + 'device/all',
                method: 'POST',
                body: { filter: { deleted: false }, params: {} }
            }, (data: Device[]) => handleSetDevices(data, devicesTypes)));
    }, [getDeviceTypes, getDevices]);

    const handleChangeSelected = (id: number, deviceTypeId: number) => {
        setDevicesOptions(oldDevicesOptions => {
            const newDevicesOptions = [...oldDevicesOptions];
            return newDevicesOptions.map(deviceOptions => {
                if (deviceOptions.id !== deviceTypeId) return deviceOptions;
                const newDeviceOptions = { ...deviceOptions };
                return {
                    ...newDeviceOptions,
                    modelsDevices: newDeviceOptions.modelsDevices.map(oldModelDevice => {
                        const newModelDevice: DeviceModelManufacturer = { ...oldModelDevice };
                        if (oldModelDevice.id !== id) return oldModelDevice.check === false ? oldModelDevice : { ...newModelDevice, check: false };
                        newModelDevice.check = !newModelDevice.check;
                        return newModelDevice;
                    })
                };
            });
        });
    };

    const selectedDevicesOptions = devicesOptions.filter(deviceOption => deviceOption.modelsDevices
        .some(modelDevice => modelDevice.check))
        .map(devicesOption => {
            return {
                ...devicesOption,
                modelsDevices: devicesOption.modelsDevices.filter(modelDevice => modelDevice.check)
            };
        });

    const handleConfirm = () => {
        let callsMade = 0;
        selectedDevicesOptions.forEach((devicesOption => devicesOption.modelsDevices.forEach(modelDevices => {
            LinkDevice({
                url: getEnvironmentApiUrl() + 'device_loan',
                method: 'POST',
                body: {
                    device_id: modelDevices.devices[0].id,
                    patient_id: patientID,
                    loan_date: (new Date()).toISOString(),
                    loan_user_id: getUser().id,
                    deleted: false,
                }
            }, () => {
                toast.success(<p>{t('device')} <strong>{modelDevices?.name.split('(')[0]}</strong> {t('assigned_correctly')}</p>);
                callsMade++;
                if (callsMade === selectedDevicesOptions.length) getAllLoanDevices();
            }, () => {
                toast.error(`${t('error_lending_device')} ${modelDevices?.name.split('(')[0]}`);
                callsMade++;
                if (callsMade === selectedDevicesOptions.length) getAllLoanDevices();
            });
        })));
        showModal(false);
    };

    return (
        <div className='dialog'>
            <h1 className='dialogTitle'>{t('select_device_assign')}</h1>
            <div className={styles.dialogContainer}>
                <div className={styles.typesContainer}>
                    {devicesOptions.map(deviceType =>
                        <DropdownSelector
                            key={deviceType.id}
                            label={deviceType.name || ''}
                            options={deviceType.modelsDevices}
                            msgNoOptions={t('no_devices_available') as string}
                            onChange={(id: number) => handleChangeSelected(id, deviceType.id)}
                        />
                    )}
                </div>
                <div className={styles.selectedDevicesContainer}>
                    <h3 className={styles.titleDevicesSelected}>{t('devices_selected')}</h3>
                    <div className={styles.devicesTypeContainer}>
                        {selectedDevicesOptions.length > 0 ? selectedDevicesOptions.map(selectedDeviceOption =>
                            <div className={styles.devicesOfTypeContainer} key={selectedDeviceOption.id}>
                                <p className={styles.nameType}>{selectedDeviceOption.description}</p>
                                {selectedDeviceOption.modelsDevices.map(modelDevice =>
                                    <p key={modelDevice.id}>{modelDevice.name.split('(')[0]}</p>
                                )}
                            </div>
                        ) :
                            <div className={styles.noData}>{t('no_devices_selected')}</div>
                        }
                    </div>
                </div>
            </div>
            <CancelConfirmButtons onCancel={() => showModal(false)} onConfirm={handleConfirm} />
        </div>
    );
};

export default DialogDeviceSelect;